import sessionService from '../services/sessionService';

/**
 * Flags de entorno de build.
 *
 * `VITE_TESTING_PHASE=true` es el build de DESARROLLO (proyecto Vercel af-dev,
 * dev-af.aitopstaff.com, WS 8081); ausente/false es PRODUCCIÓN (af, WS 8080).
 * Mismo criterio que usa App.tsx para configService y los previews por URL.
 */
export function isTestingBuild(): boolean {
  const env = import.meta.env.VITE_TESTING_PHASE;
  if (env !== undefined) return env === 'true' || (env as unknown) === true;
  return import.meta.env.DEV === true;
}

/**
 * Parte gráfica de gamificación (paisaje "la casa de Galia" + trofeos por
 * acción): en construcción — visible solo en el build de desarrollo hasta que
 * la AF entregue el arte final (acuerdo con Santiago, 2026-07-09). El resto de
 * la gamificación preexistente (niveles, racha) NO pasa por este flag.
 */
export function isGamificationUIEnabled(): boolean {
  return isTestingBuild();
}

/**
 * Modo kiosco (pantalla de recepción de la AF, acordado con Santiago 24/08):
 * `?kiosk=1` en el URL. Sesión anónima (no guarda nada), configuración
 * automática A1/Conversadora y reinicio de la conversación tras inactividad
 * (ver App.tsx). Solo build de desarrollo mientras el paquete no esté en prod.
 */
/**
 * Layout «Espace de travail» — rediseño de la interfaz, opción 1a del PDF
 * «Rediseño Galia - Opciones» (elegida por Santiago en la reunión del
 * 15/09/2026, con el ocre cambiado por el naranja de Galia #FE481B): contenido y
 * chat en dos columnas permanentes que nunca se tapan, sin efectos de vidrio, y
 * la mascota discreta en la cabecera del chat.
 *
 * Solo build de desarrollo hasta que Santiago lo apruebe. Fuera del kiosco (la
 * pantalla de recepción tiene su propia portada). `?layout=classic` vuelve a la
 * interfaz actual en dev, para comparar lado a lado.
 */
export function isWorkspaceLayoutEnabled(): boolean {
  if (typeof window === 'undefined' || !isTestingBuild()) return false;
  if (isKioskMode()) return false;
  return new URLSearchParams(window.location.search).get('layout') !== 'classic';
}

export function isKioskMode(): boolean {
  if (typeof window === 'undefined' || !isTestingBuild()) return false;
  return new URLSearchParams(window.location.search).has('kiosk');
}

/**
 * Acceso directo (fase 1, acuerdo con Santiago 24/08): el estudiante entra sin
 * pasar por Apolearn, se identifica con su correo y un código de 6 dígitos, y
 * el roster de AEC decide si puede.
 *
 * Es el comportamiento por defecto de la raíz del sitio cuando NO hay ni
 * lanzamiento LTI (`?session=` / `?token=`), ni actividad por URL (`tache_id`),
 * ni sesión LTI guardada (viva o vencida: esa sigue su camino, incluida la
 * pantalla de "sesión vencida"). `?directo=1` lo fuerza (útil para pruebas).
 * Desde el 08/09/2026 ya no hace falta el parámetro: Santiago prueba con la
 * URL limpia.
 *
 * Solo build de desarrollo mientras el paquete no esté en prod.
 * El kiosco manda sobre esto: una pantalla de recepción no pide correos.
 * Plan: docs/security/acceso-directo-fase1-plan.md
 */
/**
 * ¿Existe en este build la puerta de acceso por correo? (Solo dev, fuera del
 * kiosco.) A diferencia de isDirectAccessMode(), no mira si ESTA carga debe
 * mostrarla: sirve para ofrecerla como alternativa, p. ej. en la pantalla de
 * sesión vencida (SessionExpired).
 */
export function isDirectAccessAvailable(): boolean {
  if (typeof window === 'undefined' || !isTestingBuild()) return false;
  return !isKioskMode();
}

export function isDirectAccessMode(): boolean {
  if (typeof window === 'undefined' || !isTestingBuild()) return false;
  if (isKioskMode()) return false;
  const params = new URLSearchParams(window.location.search);
  if (params.has('directo')) return true;
  if (params.has('session') || params.has('token') || params.has('tache_id')) return false;
  return sessionService.peekStoredOrigin() !== 'lti';
}

/**
 * Identidad efímera del kiosco: los trofeos (contadores, toast, paisaje)
 * necesitan un "email" como clave, pero la sesión del kiosco es anónima a
 * propósito (no guarda conversaciones ni entra a métricas). Este id por CARGA
 * de página da esa clave solo para la capa de trofeos: vive en localStorage
 * del navegador del kiosco (Supabase no tiene fila para él, el update afecta
 * 0 filas) y el reinicio por inactividad recarga la página → visitante nuevo,
 * progreso en cero.
 */
const KIOSK_VISIT_EMAIL = `kiosco-${Math.random().toString(36).slice(2, 10)}@demo.galia`;
export function kioskVisitEmail(): string {
  return KIOSK_VISIT_EMAIL;
}
